import { PaginationControls } from "@/components/pagination-controls";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useServerPagination } from "@/hooks/use-pagination";
import {
  ContentOperationService,
  type DashboardPage,
  type DashboardRow,
} from "@/lib/api/content.operation.service";
import { useAuth } from "@/lib/auth";
import { withFromParam } from "@/lib/back-nav";
import { CONTENT_TYPES } from "@/lib/content-types";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ExternalLink, FileText, Globe, Tags } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation } from "wouter";

function ContentTypeIcon({ type }: { type: string }) {
  const Icon =
    CONTENT_TYPES[type as keyof typeof CONTENT_TYPES]?.icon ?? FileText;
  return <Icon className="h-4 w-4 text-muted-foreground" />;
}

function StatusBadge({ status }: { status: string }) {
  const isLive = status === "published";
  const isArchived = status === "archived";
  return (
    <Badge
      variant={isLive ? "default" : isArchived ? "secondary" : "outline"}
      data-testid={`badge-status-${status}`}
    >
      {status.replace(/_/g, " ")}
    </Badge>
  );
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

const statusMap: Record<string, string> = {
  pending: "submitted",
  needs_revision: "changes_requested",
  approved: "published",
  // EVO-16: approved media waits here until MediaConvert finishes or fails.
  transcoding: "transcoding",
  failed: "failed",
};

export default function ReviewPage() {
  const { user } = useAuth();
  const [location, setLocation] = useLocation();
  const [activeTab, setActiveTab] = useState("pending");

  const isCoach = user?.roles.includes("coach");

  const status = statusMap[activeTab] || "submitted";
  // EVO-59: only recent approvals, newest first, dated by approval (publish).
  const isApprovedTab = activeTab === "approved";
  const ownerId = isCoach && user?.id ? user.id : undefined;

  const [page, setPage] = useState(1);
  useEffect(() => {
    setPage(1);
  }, [status, ownerId]);

  const { data, isLoading } = useQuery<DashboardPage>({
    queryKey: ["content-review-items", { status, ownerId, page }],
    // EVO-59: approvals happen on other screens; refetch on return / tab switch.
    staleTime: 0,
    queryFn: () =>
      ContentOperationService.getDashboardItems({
        status,
        ownerId,
        page,
        limit: 10,
        ...(isApprovedTab
          ? {
              sortField: "publishedAt",
              sortDir: "desc" as const,
              publishedWithinDays: 30,
            }
          : {}),
      }),
  });

  const items = data?.items ?? [];
  const paginationProps = useServerPagination({
    page: data?.page ?? page,
    total: data?.total ?? 0,
    pageSize: data?.limit ?? 10,
    onPageChange: setPage,
  });

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto" data-testid="review-page">
      <div className="flex items-center gap-3 flex-wrap">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setLocation("/content-operations")}
          data-testid="button-back"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1
            className="text-2xl font-semibold tracking-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
            data-testid="text-page-title"
          >
            Approval Center
          </h1>
          <p className="text-sm text-muted-foreground">
            {isCoach
              ? "Review your submitted content"
              : "Review and approve content submissions"}
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList data-testid="tabs-review">
          <TabsTrigger value="pending" data-testid="tab-review-pending">
            Pending
          </TabsTrigger>
          <TabsTrigger value="needs_revision" data-testid="tab-review-revision">
            Needs Revision
          </TabsTrigger>
          <TabsTrigger value="approved" data-testid="tab-review-approved">
            Recently Approved
          </TabsTrigger>
          <TabsTrigger value="transcoding" data-testid="tab-review-transcoding">
            Transcoding
          </TabsTrigger>
          <TabsTrigger value="failed" data-testid="tab-review-failed">
            Failed
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {isLoading ? (
        <Skeleton className="h-96 w-full" />
      ) : (
        <>
          <div className="flex items-center gap-3 flex-wrap">
            <span
              className="text-sm text-muted-foreground ml-auto tabular-nums"
              data-testid="text-item-count"
            >
              {data?.total ?? 0} items
            </span>
          </div>

          <div className="border rounded-md overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]">Type</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="hidden md:table-cell">Coach</TableHead>
                  <TableHead className="hidden md:table-cell">
                    {isApprovedTab ? "Approved" : "Submitted"}
                  </TableHead>
                  <TableHead className="hidden lg:table-cell">
                    Languages
                  </TableHead>
                  <TableHead className="hidden lg:table-cell">Tags</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      className="text-center text-muted-foreground py-8"
                    >
                      No items in this queue
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item: DashboardRow) => (
                    <TableRow
                      key={`${item.contentType}-${item.id}`}
                      data-testid={`row-review-${item.id}`}
                    >
                      <TableCell>
                        <ContentTypeIcon type={item.contentType} />
                      </TableCell>
                      <TableCell
                        className="font-medium max-w-[200px] truncate"
                        data-testid={`text-title-${item.id}`}
                      >
                        {item.title}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={item.status} />
                      </TableCell>
                      <TableCell
                        className="hidden md:table-cell text-sm text-muted-foreground"
                        data-testid={`text-coach-${item.id}`}
                      >
                        {item.createdByName || "Unknown"}
                      </TableCell>
                      <TableCell
                        className="hidden md:table-cell text-sm text-muted-foreground whitespace-nowrap"
                        data-testid={`text-submitted-${item.id}`}
                      >
                        {formatRelativeTime(
                          (isApprovedTab
                            ? (item.publishedAt ?? item.approvedAt)
                            : item.submittedAt) ?? item.updatedAt,
                        )}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        <div className="flex items-center gap-1">
                          <Globe className="h-3 w-3 text-muted-foreground" />
                          <span
                            className="text-sm tabular-nums"
                            data-testid={`text-languages-${item.id}`}
                          >
                            {item.languageCount}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        <div className="flex items-center gap-1">
                          <Tags className="h-3 w-3 text-muted-foreground" />
                          <span
                            className="text-sm tabular-nums"
                            data-testid={`text-tags-${item.id}`}
                          >
                            {item.tagCount}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() =>
                            setLocation(withFromParam(item.editUrl, location))
                          }
                          data-testid={`button-open-${item.id}`}
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <PaginationControls
              currentPage={paginationProps.currentPage}
              totalPages={paginationProps.totalPages}
              totalItems={paginationProps.totalItems}
              startIndex={paginationProps.startIndex}
              endIndex={paginationProps.endIndex}
              canGoNext={paginationProps.canGoNext}
              canGoPrev={paginationProps.canGoPrev}
              onPageChange={paginationProps.setPage}
              onNext={paginationProps.nextPage}
              onPrev={paginationProps.prevPage}
            />
          </div>

          {isCoach && (
            <p
              className="text-sm text-muted-foreground"
              data-testid="text-coach-notice"
            >
              You are viewing your own submitted content. Approval actions
              require Admin or Super Admin access.
            </p>
          )}
        </>
      )}
    </div>
  );
}
